import type { ComponentProps } from "react";
import { NumberField, Select } from "../components/rumonai";
import { FormBlockView, type FieldRendererProps } from "../panel/form";
import "./service-pricing.css";

export interface PricingField { units: Record<string, string> }

function objectOf(value: unknown): Record<string, unknown> | null {
  let parsed = value;
  if (typeof parsed === "string") {
    if (!parsed.trim()) return {};
    try { parsed = JSON.parse(parsed); } catch { return null; }
  }
  return parsed && typeof parsed === "object" && !Array.isArray(parsed)
    ? parsed as Record<string, unknown> : null;
}

function validate(value: unknown, spec: PricingField): void {
  const current = objectOf(value);
  if (!current) throw new Error("费率需要有效的币种、单价和计价单位。");
  if (!Object.keys(current).length) return;
  const units = Object.keys(spec.units).filter(unit => unit in current);
  if (units.length !== 1) throw new Error("请选择此服务支持的计价单位；每份费率只能用一个单位。");
  if (!/^[A-Z]{3}$/.test(String(current.currency ?? "").trim().toUpperCase()))
    throw new Error("币种请填写三位代码，例如 USD 或 CNY。");
  const rate = current[units[0]];
  if (typeof rate !== "number" || !Number.isFinite(rate) || rate < 0)
    throw new Error("单价请填写大于或等于零的数字；留空不能当作免费。");
}

function PricingControl({ spec, ...props }: FieldRendererProps & { spec: PricingField }) {
  const { field, id, value, onChange, describedBy, disabled } = props;
  const stored = objectOf(value);
  const current = stored ?? {};
  const supported = Object.keys(spec.units);
  const selected = supported.filter(unit => unit in current);
  const unit = selected.length === 1 ? selected[0] : "";
  const unsupported = selected.length > 1 || (!unit && Object.keys(current).some(key => key.startsWith("per_")));
  const put = (patch: Record<string, unknown>) => onChange(JSON.stringify({ ...current, ...patch }, null, 2));
  return <div role="group" aria-label={field.label} className="service-pricing">
    <p className="micro">自带服务费用按此费率记录，与平台可用额度分别统计。修改只影响后续请求；不会重算历史未知调用。</p>
    {!stored ? <p role="status">当前费率无法读取，原值仍保留；请核对后再改。</p> : null}
    {unsupported ? <p role="status">当前费率的计价单位不在此服务支持范围，原值保留；选择合法单位才会修改它。</p> : null}
    <label htmlFor={`${id}-currency`}>币种</label>
    <span className="pa-field-shell"><input id={`${id}-currency`} name={`${field.key}.currency`} type="text" value={String(current.currency ?? "")} maxLength={3} disabled={disabled}
      placeholder="例如 USD" aria-describedby={describedBy}
      onChange={event => put({ currency: event.target.value.toUpperCase() })} /><span className="pa-paint-edge" aria-hidden="true" /></span>
    <label htmlFor={`${id}-unit`}>计价单位</label>
    <Select id={`${id}-unit`} name={`${field.key}.unit`} label="计价单位" value={unit} aria-describedby={describedBy} disabled={disabled} onValueChange={chosen => {
      const next = { ...current };
      const rate = unit ? current[unit] : "";
      for (const key of supported) delete next[key];
      if (chosen) next[chosen] = rate;
      onChange(JSON.stringify(next, null, 2));
    }} placeholder="请选择计价单位" items={Object.entries(spec.units).map(([value, label]) => ({ value, label }))} />
    <label htmlFor={`${id}-rate`}>单价</label>
    <NumberField id={`${id}-rate`} name={`${field.key}.rate`} label="单价" min={0} step="any" value={unit ? String(current[unit] ?? "") : ""}
      aria-describedby={describedBy} disabled={disabled || !unit}
      onValueChange={next => put({ [unit]: next ?? "" })} />
    <p className="micro">空费率表示未定价；只有明确填写 0 才表示免费。</p>
  </div>;
}

/** Declared service tariffs use ordinary controls and the existing settings write path. */
export function ServiceConfigForm({ pricingFields = {}, ...props }: ComponentProps<typeof FormBlockView> & {
  pricingFields?: Record<string, PricingField>;
}) {
  const check = (params: Record<string, unknown>) => {
    for (const [key, spec] of Object.entries(pricingFields)) {
      if (key in params && JSON.stringify(params[key]) !== JSON.stringify(props.initialValues?.[key])) validate(params[key], spec);
    }
  };
  return <FormBlockView {...props}
    fieldRenderers={Object.fromEntries(Object.entries(pricingFields).map(([key, spec]) => [key,
      (control: FieldRendererProps) => <PricingControl {...control} spec={spec} />]))}
    write={props.write ? async (...args) => { check(args[1]); await props.write!(...args); } : undefined}
    runAction={async (...args) => { check(args[1]); return props.runAction(...args); }} />;
}
